/**
 * The pool half of the register-a-team form, extracted from the route so it
 * can be asserted directly. The pool step only appears once a division has
 * been picked — a click — and `renderToStaticMarkup` never runs effects or
 * handlers (see the testing conventions), so the route's own test cannot reach
 * this logic at all. Pure functions can be reached without a DOM.
 *
 * The rule these three encode together: a coach picks the pool they are
 * registering for, but only when the organizer has built pools to pick from.
 * Pools are created whenever the organizer gets to it — often after the
 * registration window is already open — so a division with none is ordinary,
 * not an error, and the server accepts a null pool for exactly that case.
 */
import type { Pool } from "./types";

/**
 * The pools a division offers, in the order the organizer set. `order` is the
 * organizer's own sequence; name breaks a tie so two pools sharing an order do
 * not swap places between renders.
 */
export function poolsForDivision(pools: Pool[], divisionId: string): Pool[] {
  if (!divisionId) return [];
  return pools
    .filter((p) => p.divisionId === divisionId)
    .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
}

/**
 * The pick, but only while the dropdown is still offering it — the same guard
 * the tournament field applies to `?tournament=`. Changing division clears the
 * held value, but an organizer deleting a pool or moving it to another
 * division between the bundle fetch and submit does not: the `<select>` would
 * fall back to its placeholder while the form quietly kept posting an id that
 * no longer resolves.
 */
export function resolvePoolId(pools: Pool[], picked: string): string {
  return pools.some((p) => p.id === picked) ? picked : "";
}

/**
 * Whether the pool question has been answered. Vacuously true when there is
 * nothing to answer — requiring a pool unconditionally would make a tournament
 * whose pools do not exist yet impossible to register for, which is a
 * regression on a flow that worked before pools were asked about at all.
 */
export function poolReady(pools: Pool[], poolId: string): boolean {
  return pools.length === 0 || Boolean(poolId);
}
